/**
 * Tela pública do aprovador — assinatura online com recibo por e-mail.
 *
 * URL: /inovacao/aprovacao/:token
 * Ao aprovar/rejeitar:
 *   - Backend grava IP, hash de user-agent e hash SHA-256 da decisão
 *   - Dispara e-mail de recibo ao próprio aprovador com o hash (ID da assinatura)
 * Após assinatura, tela mostra comprovante + botão imprimir.
 */

import { useBranding, logoHeader } from '../../context/BrandingContext';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { CheckCircle, XCircle, Printer, Mail, ShieldCheck } from 'lucide-react';
import { aprovacaoPublicaAPI } from '../../services/inovacaoAPI';

const BRAND = 'var(--brand, #20a490)';

export default function AprovacaoPublica() {
  const { token } = useParams();
  const { branding } = useBranding();

  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(null);
  const [comentario, setComentario] = useState('');
  const [processando, setProcessando] = useState(false);

  async function carregar() {
    const d = await aprovacaoPublicaAPI.obter(token);
    setDados(d);
  }

  useEffect(() => {
    carregar().catch((e) => {
      setErro(e.response?.data?.detail?.message || 'Este link de aprovação é inválido ou expirou.');
    });
  }, [token]);

  async function decidir(decisao) {
    if (!window.confirm(
      decisao === 'aprovada'
        ? 'Confirmar aprovação? Esta ação será registrada como sua assinatura digital.'
        : 'Confirmar rejeição? Esta ação será registrada como sua assinatura digital.'
    )) return;

    setProcessando(true);
    try {
      await aprovacaoPublicaAPI.responder(token, decisao, comentario || null);
      toast.success('Assinatura registrada! Um comprovante foi enviado ao seu e-mail.');
      await carregar();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || 'Erro');
    } finally {
      setProcessando(false);
    }
  }

  if (erro) {
    return (
      <div style={{ maxWidth: 480, margin: '60px auto', padding: 32, textAlign: 'center',
                    background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
        <XCircle size={48} style={{ color: '#e74c3c', marginBottom: 12 }} />
        <h2 style={{ color: '#2c3e50', margin: '0 0 8px' }}>Link indisponível</h2>
        <p style={{ color: '#7f8c8d', margin: 0 }}>{erro}</p>
        <p style={{ color: '#7f8c8d', marginTop: 8 }}>Em caso de dúvida, contate o setor de Inovação.</p>
      </div>
    );
  }
  if (!dados) return <div style={{ padding: 40 }}>Carregando…</div>;

  const ap = dados.aprovacao;
  const jaRespondeu = ap.estado !== 'pendente';
  const cor = ap.estado === 'aprovada' ? '#27ae60'
            : ap.estado === 'rejeitada' ? '#e74c3c'
            : '#f39c12';

  return (
    <div style={estilos.pagina}>
      <style>{CSS_PRINT}</style>

      <div style={estilos.folha} className="doc-folha">
        {/* Cabeçalho */}
        <header style={estilos.cabecalho} className="doc-cabecalho">
          <img src={logoHeader(branding)} alt={branding.nome_operadora} style={estilos.logo} />
          <div style={{ flex: 1 }}>
            <h1 style={estilos.titulo}>Documento para Aprovação</h1>
            <p style={estilos.subtitulo}>Governança de Novas Iniciativas · {branding.nome_operadora}</p>
          </div>
          {jaRespondeu && (
            <button onClick={() => window.print()} style={estilos.btnSecundario} className="no-print">
              <Printer size={14} /> Imprimir recibo
            </button>
          )}
        </header>

        {/* Dados da análise */}
        <section style={{ marginBottom: 24 }}>
          <h2 style={estilos.tituloSecao}>{dados.analise_titulo}</h2>
          <dl style={estilos.dl}>
            <KV label="Seu papel nesta aprovação" value={ap.papel_descricao} />
            <KV label="Setor solicitante" value={dados.setor_solicitante?.nome} />
            <KV label="Solicitante" value={dados.responsavel_respostas?.nome} />
            <KV label="Executor GNI" value={dados.executor_gni?.nome} />
            {dados.analise_score != null && (
              <KV label="Score de oportunidade" value={`${dados.analise_score} / 100`} />
            )}
            {dados.analise_horas_mensais && (
              <KV label="Horas mensais estimadas" value={`${dados.analise_horas_mensais}h`} />
            )}
          </dl>

          {dados.analise_resumo_executivo && (
            <>
              <h3 style={estilos.subSecao}>Resumo executivo</h3>
              <p style={estilos.paragrafo}>{dados.analise_resumo_executivo}</p>
            </>
          )}
          {dados.analise_objetivo && (
            <>
              <h3 style={estilos.subSecao}>Objetivo</h3>
              <p style={estilos.paragrafo}>{dados.analise_objetivo}</p>
            </>
          )}
        </section>

        {/* Estado: assinado ou pendente */}
        {jaRespondeu ? (
          <ReciboAssinatura ap={ap} cor={cor} />
        ) : (
          <FormAssinatura comentario={comentario}
                          onChange={setComentario}
                          onDecidir={decidir}
                          processando={processando} />
        )}

        <footer style={estilos.rodape}>
          {!jaRespondeu && (
            <p style={{ fontSize: '0.8rem', color: '#95a5a6', marginBottom: 8 }}>
              <ShieldCheck size={12} style={{ verticalAlign: 'middle' }} /> Ao clicar em
              <b> aprovar</b> ou <b>rejeitar</b>, seu IP, navegador e um hash criptográfico
              dos dados serão registrados como prova da sua assinatura. Um comprovante será
              enviado automaticamente ao seu e-mail.
            </p>
          )}
          Governança de Novas Iniciativas · {branding.nome_operadora}
        </footer>
      </div>
    </div>
  );
}

function ReciboAssinatura({ ap, cor }) {
  const icone = ap.estado === 'aprovada' ? <CheckCircle size={40} color={cor} />
              : ap.estado === 'rejeitada' ? <XCircle size={40} color={cor} />
              : null;
  const textoDec = ap.estado === 'aprovada' ? 'APROVOU' : 'REJEITOU';

  return (
    <section style={{ ...estilos.secaoRecibo, borderColor: cor }}>
      <div style={{ textAlign: 'center' }}>
        {icone}
        <h2 style={{ margin: '8px 0', color: cor, fontSize: '1.3rem' }}>
          Documento {ap.estado}
        </h2>
        <p style={{ color: '#555', margin: 0 }}>
          Você <b style={{ color: cor }}>{textoDec}</b> este documento.
        </p>
      </div>

      <hr style={{ border: 'none', borderTop: '1px solid #ecf0f1', margin: '20px 0' }} />

      <h3 style={{ margin: '0 0 12px', color: '#2c3e50', fontSize: '0.95rem' }}>
        Comprovante de assinatura
      </h3>

      <dl style={estilos.dl}>
        <KV label="Data e hora" value={new Date(ap.respondido_em).toLocaleString('pt-BR')} />
        {ap.ip_origem && <KV label="IP de origem" value={<code>{ap.ip_origem}</code>} />}
        {ap.comentario && <KV label="Seu comentário" value={ap.comentario} />}
        {ap.hash_decisao && (
          <>
            <dt style={estilos.dt}>ID da assinatura (SHA-256):</dt>
            <dd style={{ ...estilos.dd, wordBreak: 'break-all', fontFamily: 'monospace',
                         fontSize: '0.75rem', color: '#555' }}>
              {ap.hash_decisao}
            </dd>
          </>
        )}
      </dl>

      <div style={{ marginTop: 16, padding: 12, background: '#eafaf1', borderRadius: 4,
                    fontSize: '0.85rem', color: '#1e8449',
                    display: 'flex', alignItems: 'center', gap: 8 }}>
        <Mail size={16} />
        <span>Um <b>recibo completo</b> foi enviado ao seu e-mail.</span>
      </div>

      <p style={{ marginTop: 16, fontSize: '0.75rem', color: '#95a5a6', lineHeight: 1.5 }}>
        O ID da assinatura é um lacre digital: um hash gerado a partir da análise, do usuário, da decisão e do momento exato. Alterar qualquer um desses dados depois invalida o hash.
      </p>
    </section>
  );
}

function FormAssinatura({ comentario, onChange, onDecidir, processando }) {
  return (
    <section style={estilos.secaoForm}>
      <h3 style={{ margin: '0 0 12px', color: '#2c3e50' }}>Sua decisão</h3>
      <label style={{ fontWeight: 500, color: '#555', fontSize: '0.9rem' }}>
        Comentário (opcional, mas recomendado)
      </label>
      <textarea value={comentario}
                onChange={(e) => onChange(e.target.value)}
                placeholder="Justifique sua decisão. Ficará registrado no documento."
                style={{
                  width: '100%', padding: 12, marginTop: 6, borderRadius: 6,
                  border: '1px solid #ccc', minHeight: 100, fontFamily: 'inherit',
                  fontSize: '0.9rem',
                }} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 16 }}>
        <button onClick={() => onDecidir('aprovada')} disabled={processando}
                style={{ ...estilos.btnAssinar, background: '#27ae60' }}>
          <CheckCircle size={20} /> Aprovar e assinar
        </button>
        <button onClick={() => onDecidir('rejeitada')} disabled={processando}
                style={{ ...estilos.btnAssinar, background: '#e74c3c' }}>
          <XCircle size={20} /> Rejeitar e assinar
        </button>
      </div>
    </section>
  );
}

function KV({ label, value }) {
  return (
    <>
      <dt style={estilos.dt}>{label}:</dt>
      <dd style={estilos.dd}>{value}</dd>
    </>
  );
}

const estilos = {
  pagina: { background: '#eef2f5', minHeight: '100vh', padding: '32px 16px' },
  folha: {
    maxWidth: 780, margin: '0 auto', background: '#fff',
    padding: '48px 56px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: 8,
  },
  cabecalho: {
    display: 'flex', alignItems: 'center', gap: 20,
    paddingBottom: 20, borderBottom: `3px solid ${BRAND}`, marginBottom: 28,
  },
  logo: { height: 56 },
  titulo: { margin: 0, color: '#2c3e50', fontSize: '1.5rem' },
  subtitulo: { margin: '4px 0 0', color: '#7f8c8d', fontSize: '0.85rem' },
  btnSecundario: {
    background: '#fff', color: '#555', padding: '8px 12px',
    border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer',
    fontSize: '0.85rem', display: 'inline-flex', gap: 6, alignItems: 'center',
  },
  tituloSecao: { margin: 0, color: '#2c3e50', fontSize: '1.2rem' },
  subSecao: { margin: '20px 0 8px', color: '#2c3e50', fontSize: '1rem' },
  paragrafo: { color: '#444', lineHeight: 1.7, margin: 0, whiteSpace: 'pre-wrap' },
  dl: { display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '8px 20px', margin: '12px 0' },
  dt: { fontWeight: 600, color: '#555' },
  dd: { margin: 0, color: '#2c3e50' },
  secaoRecibo: {
    padding: 24, background: '#f8f9fa', borderRadius: 8,
    borderLeft: '4px solid', marginBottom: 24,
  },
  secaoForm: {
    padding: 24, background: '#fffbea', borderRadius: 8,
    borderLeft: '4px solid #f39c12', marginBottom: 24,
  },
  btnAssinar: {
    color: '#fff', padding: 16, border: 'none', borderRadius: 6,
    cursor: 'pointer', fontSize: '1rem', fontWeight: 500,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
  },
  rodape: {
    marginTop: 32, paddingTop: 16, borderTop: '1px solid #ecf0f1',
    color: '#95a5a6', fontSize: '0.8rem', textAlign: 'center',
  },
};

const CSS_PRINT = `
@media print {
  body { background: white !important; }
  .no-print { display: none !important; }
  .doc-folha { max-width: 100% !important; box-shadow: none !important; padding: 20px 0 !important; }
  .doc-cabecalho { page-break-after: avoid; }
}
`;
